import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * T-0033 / E04-F02 — override-capture client.
 *
 * Surface:
 *   - POST /api/v1/overrides                          capture an override
 *   - GET  /api/v1/overrides?register_entry_id=...    list history
 *   - GET  /api/v1/config/override-taxonomy           current taxonomy
 *
 * The structured override is the primitive that drives the E03-F03 re-eval
 * flow. Every override write co-emits `override.captured`, which the
 * T-0051 synthesis worker subscribes to. The UI does NOT call the worker
 * directly; instead it issues the override and then opens the re-eval
 * tray (`lib/api/reevaluations.ts`) which polls / streams the resulting
 * `ai_reevaluation_runs` row.
 *
 * Idempotency: the `POST /api/v1/overrides` route accepts the standard
 * `Idempotency-Key` header (scope = engagement_id + endpoint_path + key,
 * 24h dedup per cross-cutting §2). The override modal mints a fresh key
 * per save attempt so the user can retry on transient errors without
 * double-writing on success.
 *
 * Taxonomy: the v1 taxonomy is hardcoded server-side (see
 * `apps/api/src/services/overrides.ts → OVERRIDE_REASON_TAXONOMY_V1`).
 * The eventual T-0078-or-later switch to Azure App Configuration will be
 * transparent to this client — the endpoint shape stays the same.
 */

export const OverrideTaxonomyTagSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
});
export type OverrideTaxonomyTag = z.infer<typeof OverrideTaxonomyTagSchema>;

export const OverrideTaxonomySchema = z.object({
  tags: z.array(OverrideTaxonomyTagSchema),
});
export type OverrideTaxonomy = z.infer<typeof OverrideTaxonomySchema>;

export function getOverrideTaxonomy(): Promise<OverrideTaxonomy> {
  return apiClient('/api/v1/config/override-taxonomy', OverrideTaxonomySchema);
}

/**
 * React Query hook for the override taxonomy. Cached aggressively — the
 * taxonomy changes at most once per release window (and per OQ-Q, only on
 * an admin-driven Azure App Configuration edit). The cache key has no
 * variables so every modal instance shares the same payload.
 */
export function useOverrideTaxonomyQuery() {
  return useQuery({
    queryKey: ['config', 'override-taxonomy'] as const,
    queryFn: getOverrideTaxonomy,
    // Effectively immutable for the user's session.
    staleTime: 30 * 60 * 1000,
  });
}

export interface CaptureOverrideInput {
  register_entry_id: string;
  field_id?: string | null;
  reason_tag: string;
  rationale: string;
  new_value: Record<string, unknown>;
  source_document_id?: string | null;
}

export const CaptureOverrideResultSchema = z.object({
  id: z.string().uuid(),
  register_entry_id: z.string().uuid(),
  origin: z.enum([
    'ai_extracted_confirmed_as_is',
    'ai_extracted_edited_by_consultant',
    'manually_authored',
  ]),
});
export type CaptureOverrideResult = z.infer<typeof CaptureOverrideResultSchema>;

export function captureOverride(
  input: CaptureOverrideInput,
  idempotencyKey?: string,
): Promise<CaptureOverrideResult> {
  return apiClient('/api/v1/overrides', CaptureOverrideResultSchema, {
    method: 'POST',
    body: input,
    ...(idempotencyKey ? { idempotencyKey } : {}),
  });
}

export const OverrideHistoryItemSchema = z.object({
  id: z.string().uuid(),
  reason_tag: z.string(),
  rationale: z.string(),
  original_value: z.unknown(),
  new_value: z.unknown(),
  field_id: z.string().nullable(),
  source_document_id: z.string().nullable(),
  original_ai_confidence: z.number().nullable(),
  created_by: z.string(),
  created_at: z.string(),
});
export type OverrideHistoryItem = z.infer<typeof OverrideHistoryItemSchema>;

export const OverrideHistorySchema = z.object({
  items: z.array(OverrideHistoryItemSchema),
});
export type OverrideHistory = z.infer<typeof OverrideHistorySchema>;

export function listOverrides(registerEntryId: string): Promise<OverrideHistory> {
  return apiClient('/api/v1/overrides', OverrideHistorySchema, {
    query: { register_entry_id: registerEntryId },
  });
}

/**
 * Override-capture mutation. The caller passes the engagement id so the
 * mutation can invalidate the engagement-scoped register caches after a
 * successful write (the override projection mutates the register entry's
 * payload). Generates a fresh idempotency key per submit attempt — the
 * server scopes the dedup to engagement + endpoint + key, so re-issuing
 * the same key is the retry path.
 */
export function useCaptureOverrideMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CaptureOverrideInput) => {
      const idemKey =
        typeof globalThis.crypto?.randomUUID === 'function'
          ? globalThis.crypto.randomUUID()
          : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return captureOverride(input, idemKey);
    },
    onSuccess: () => {
      if (engagementId) {
        // Invalidate every register cache for the engagement so the next
        // refetch reflects the projected payload + origin flip + new
        // `latest_override_id` pointer.
        void qc.invalidateQueries({ queryKey: ['registers', engagementId] });
      }
    },
  });
}
